import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { apiClient } from "./api-client";

interface CartItem {
  id: string;
  catalog_item_id: string;
  quantity: number;
  unit_price_cents: number;
  title: string;
  slug: string;
}

interface CartState {
  cartId: string | null;
  items: CartItem[];
  loading: boolean;
  refresh: () => Promise<void>;
  addItem: (catalogItemId: string, quantity?: number) => Promise<void>;
}

const CartContext = createContext<CartState | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartId, setCartId] = useState<string | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<{ cartId: string; items: CartItem[] }>("/consumer/cart");
      setCartId(data.cartId);
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, []);

  const addItem = useCallback(
    async (catalogItemId: string, quantity = 1) => {
      await apiClient.post("/consumer/cart", { catalogItemId, quantity });
      await refresh();
    },
    [refresh],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <CartContext.Provider value={{ cartId, items, loading, refresh, addItem }}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart deve ser usado dentro de CartProvider");
  return ctx;
}
